// src/components/BgRemover.js
import React, { useState, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import {
  Container,
  Alert,
  Grid,
  Button,
  Paper,
  Typography,
  Popover,
  Box,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@mui/material/styles";
import GlowPaper from "./GlowPaper";
import ImageUploadSection from "./ImageUploadSection";
import ImagePreviewSection from "./ImagePreviewSection";
import SidebarSection from "./SidebarSection";
import styles from "./BgRemover.module.css";
import TopHeader from "../TopHeader";
import {
  Download,
  CloudUpload,
  Palette,
  Brush,
  Settings,
  AddPhotoAlternate,
} from "@mui/icons-material";
import SwipeableEdgeDrawer from "./SwipeableEdgeDrawer";

const BgRemover = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [processedImage, setProcessedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [unsplashImages, setUnsplashImages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editSettings, setEditSettings] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    backgroundBlur: 0,
    shadowIntensity: 0,
  });
  console.log(editSettings.shadowIntensity);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showUploadSection, setShowUploadSection] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const fileInputRef = useRef(null);
  const previewRef = useRef(null);
  const userToken =
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiI2Nzk5ZjAyZjQ1MGM4N2M0ZGY5ODcyNTQiLCJpYXQiOjE3MzgzMTk4MTN9.iJiZBqUHPQuFxYxE5qN1ovkbIdTcMNau6ZD2bL08ya4";

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setLoading(true);
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axios.post(
        "https://easyramini.onrender.com/user/bgRemover/generate",
        formData,
        {
          headers: {
            "x-user-token": userToken,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status) {
        setProcessedImage(response.data.data.img.url);
        setError("");
        setShowUploadSection(false);
      } else {
        setError(response.data.message || "Background removal failed");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Server error. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearchUnsplash = async (page = 1) => {
    if (!searchQuery) return;

    try {
      const response = await axios.get(
        `https://api.unsplash.com/search/photos?query=${searchQuery}&page=${page}&per_page=10&client_id=e8a730dd88a1ca594c2f2c3c10d2d5de2b91ea6e29cc7a64bd6c09b60921086a`
      );
      setUnsplashImages(response.data.results);
      setTotalPages(response.data.total_pages);
      setCurrentPage(page);
    } catch (err) {
      setError("Failed to fetch Unsplash images");
    }
  };

  const handleDownload = async (format) => {
    if (!previewRef.current || !processedImage) return;

    setLoading(true);

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = processedImage;

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      if (backgroundImage) {
        const bgImg = new Image();
        bgImg.crossOrigin = "anonymous";
        bgImg.src = backgroundImage;
        await new Promise((resolve, reject) => {
          bgImg.onload = resolve;
          bgImg.onerror = reject;
        });

        // Apply background blur
        ctx.filter = `blur(${editSettings.backgroundBlur}px)`;
        ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
        ctx.filter = "none";
      } else {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.filter = `
        brightness(${editSettings.brightness}%)
        contrast(${editSettings.contrast}%)
        saturate(${editSettings.saturation}%)
        grayscale(${editSettings.grayscale || 0}%)
        sepia(${editSettings.sepia || 0}%)
      `;

      // Corrected shadow implementation
      if (editSettings.shadowIntensity > 0) {
        ctx.shadowColor = "rgba(0,0,0,0.5)";
        ctx.shadowBlur = 10; // Fixed blur to match CSS preview
        ctx.shadowOffsetX = editSettings.shadowIntensity;
        ctx.shadowOffsetY = editSettings.shadowIntensity;
      }

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const link = document.createElement("a");
      link.download = `processed-image.${format}`;
      link.href = canvas.toDataURL(`image/${format}`, 1.0);
      link.click();
    } catch (error) {
      console.error("Export error:", error);
      setError("Failed to export image. Please try again.");
    } finally {
      setLoading(false);
      setAnchorEl(null);
    }
  };

  const applyFilters = () => ({
    filter: `
      brightness(${editSettings.brightness}%)
      contrast(${editSettings.contrast}%)
      saturate(${editSettings.saturation}%)
      grayscale(${editSettings.grayscale || 0}%)
      sepia(${editSettings.sepia || 0}%)
            drop-shadow(${editSettings.shadowIntensity}px ${
      editSettings.shadowIntensity
    }px 10px rgba(0, 0, 0, 0.5))
    `,
  });

  const handleDownloadTransparent = async () => {
    try {
      const imageUrl = processedImage;
      const filename = imageUrl.split("/").pop();

      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  const gradientButtonStyle = {
    background:
      "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)",
    color: "#fff",
    fontWeight: 600,
    letterSpacing: "0.5px",
    padding: "12px 24px",
    borderRadius: "12px",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(99, 102, 241, 0.3)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(120deg, transparent, rgba(255,255,255,0.2), transparent)",
      transition: "0.5s",
    },
    "&:hover::before": {
      left: "100%",
    },
  };

  return (
    <div className={styles.mainContainer}>
      <TopHeader />
      <Container className={styles.container}>
        <AnimatePresence>
          {showUploadSection ? (
              <GlowPaper elevation={3}>
                <ImageUploadSection
                  loading={loading}
                  isDragActive={false}
                  onImageUpload={handleImageUpload}
                  fileInputRef={fileInputRef}
                  selectedFile={selectedFile}
                  icon={
                    <CloudUpload
                      sx={{ fontSize: 48, color: "rgba(255,255,255,0.9)" }}
                    />
                  }
                />
              </GlowPaper>
          ) : (
            <motion.div
              key="upload-button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={styles.uploadButtonContainer}
            >
              <div className={styles.buttonGroup}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setShowUploadSection(true);
                    setSelectedFile(null);
                    setError("");
                  }}
                  sx={gradientButtonStyle}
                  startIcon={<AddPhotoAlternate />}
                >
                  Upload New Image
                </Button>
                {processedImage && (
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      ...gradientButtonStyle,
                      background:
                        "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)",
                    }}
                    onClick={(e) => setExportAnchorEl(e.currentTarget)}
                    startIcon={<Download />}
                  >
                    Export Design
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Popover
          open={Boolean(exportAnchorEl)}
          anchorEl={exportAnchorEl}
          onClose={() => setExportAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          PaperProps={{
            sx: {
              background: "rgba(15, 23, 42, 0.95)",
              backdropFilter: "blur(24px)",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.15)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
              width: isMobile ? "90vw" : "400px",
              marginTop: "16px",
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: "#fff",
                fontWeight: 700,
                background: "linear-gradient(45deg, #6366f1, #ec4899)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Export Settings
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Download />}
                sx={gradientButtonStyle}
                onClick={handleDownloadTransparent}
              >
                Transparent PNG
              </Button>

              <Button
                fullWidth
                variant="contained"
                startIcon={<Download />}
                sx={gradientButtonStyle}
                onClick={() => handleDownload("png")}
              >
                Standard PNG
              </Button>

              <Button
                fullWidth
                variant="contained"
                startIcon={<Download />}
                sx={gradientButtonStyle}
                onClick={() => handleDownload("jpg")}
              >
                High Quality JPG
              </Button>
            </Box>

            <Typography
              variant="caption"
              sx={{
                mt: 3,
                display: "block",
                color: "rgba(255,255,255,0.7)",
                textAlign: "center",
              }}
            >
              Export resolution up to 4K (3840×2160)
            </Typography>
          </Box>
        </Popover>
        <br />
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
            >
              <Alert severity="error" className={styles.alert}>
                {error}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {processedImage && (
          <Grid container spacing={3} className={styles.resultSection}>
            <ImagePreviewSection
              processedImage={processedImage}
              backgroundColor={backgroundColor}
              backgroundImage={backgroundImage}
              applyFilters={applyFilters}
              previewRef={previewRef}
              backgroundBlur={editSettings.backgroundBlur}
            />
            {!isMobile && (
              <SidebarSection
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                backgroundColor={backgroundColor}
                setBackgroundColor={setBackgroundColor}
                backgroundImage={backgroundImage}
                setBackgroundImage={setBackgroundImage}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                unsplashImages={unsplashImages}
                editSettings={editSettings}
                setEditSettings={setEditSettings}
                handleSearchUnsplash={handleSearchUnsplash}
                currentPage={currentPage}
                totalPages={totalPages}
              />
            )}
            {isMobile && (
              <SwipeableEdgeDrawer
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                backgroundColor={backgroundColor}
                setBackgroundColor={setBackgroundColor}
                backgroundImage={backgroundImage}
                setBackgroundImage={setBackgroundImage}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                unsplashImages={unsplashImages}
                editSettings={editSettings}
                setEditSettings={setEditSettings}
                handleSearchUnsplash={handleSearchUnsplash}
                currentPage={currentPage}
                totalPages={totalPages}
              />
            )}
          </Grid>
        )}
      </Container>
    </div>
  );
};

export default BgRemover;